"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { MapPin, Globe, Compass, Map, CircleCheck, PlaneTakeoff } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const destinations = [{
  id: "uk",
  name: "Ceremony Garden",
  price: "300",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_ceremony_garden.png"
}, {
  id: "usa",
  name: "Grand Ballroom",
  price: "300",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_grand_ballroom.png"
}, {
  id: "europe",
  name: "The Terrace",
  price: "150",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_outdoor_terrace.png"
}, {
  id: "canada",
  name: "Bridal Suite",
  price: "20",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_bridal_suite.png"
}];
const handlingPoints = [{
  destId: "uk",
  point: "Lush Manicured Lawns"
}, {
  destId: "uk",
  point: "Floral Archway Setup"
}, {
  destId: "uk",
  point: "Outdoor Sound System"
}, {
  destId: "usa",
  point: "Modern Décor Style"
}, {
  destId: "usa",
  point: "Crystal Chandeliers"
}, {
  destId: "usa",
  point: "Spacious Dance Floor"
}, {
  destId: "europe",
  point: "Cocktail Hour Space"
}, {
  destId: "europe",
  point: "Sunset View Vistas"
}, {
  destId: "europe",
  point: "Heated Lounge Area"
}, {
  destId: "canada",
  point: "Private Preparation"
}, {
  destId: "canada",
  point: "Champagne Service"
}, {
  destId: "canada",
  point: "Luxury Vanity Area"
}];
const getDestinationIcon = (id: string) => {
  switch (id) {
    case "uk":
      return <MapPin className="size-10 text-secondary mb-4 drop-shadow-md" />;
    case "usa":
      return <Globe className="size-10 text-secondary mb-4 drop-shadow-md" />;
    case "europe":
      return <Compass className="size-10 text-secondary mb-4 drop-shadow-md" />;
    case "canada":
      return <Map className="size-10 text-secondary mb-4 drop-shadow-md" />;
    default:
      return <PlaneTakeoff className="size-10 text-secondary mb-4 drop-shadow-md" />;
  }
};
export default function OurSignatureSpaces() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224928" id="our-signature-spaces" className="bg-background py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerReveal.ref} className={`text-left max-w-3xl mx-auto mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Badge variant="outline" className="mb-4 border-secondary text-secondary px-4 py-1 text-sm tracking-widest uppercase bg-secondary/5 font-mono">
            The Estate
          </Badge>
          <h2 className="font-default text-5xl md:text-6xl font-medium text-foreground mb-6 tracking-tight">
            Our Signature Spaces
          </h2>
          <p className="font-sans text-muted-foreground text-lg md:text-xl leading-relaxed max-w-2xl">
            Discover an elegant, customizable setting for your special day. From our lush gardens to the grand ballroom, every corner is designed for unforgettable moments.
          </p>
        </div>

        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {destinations.map((dest, i) => <div key={dest.id} data-index={i} className="group relative h-[420px] w-full overflow-hidden rounded-md bg-card shadow-md transition-all duration-700 hover:shadow-xl hover:-translate-y-2 border border-border/30">
              {}
              <img src={dest.image} alt={`${dest.name} Architecture`} className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-1000 group-hover:scale-105 group-hover:grayscale-0" />
              
              {}
              <div className="absolute inset-0 bg-primary/40 transition-colors duration-700 group-hover:bg-primary/20" />

              {}
              <div className="absolute right-4 top-4 z-20">
                <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs font-mono tracking-widest shadow-sm px-3 py-1.5 border-none">
                  CAPACITY: {dest.price}
                </Badge>
              </div>

              {}
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center">
                <div className="transform transition-transform duration-700 group-hover:-translate-y-4">
                  {getDestinationIcon(dest.id)}
                  <h3 className="font-default text-3xl font-medium text-white tracking-wide drop-shadow-lg">
                    {dest.name}
                  </h3>
                </div>

                {}
                <div className="absolute bottom-10 left-0 right-0 px-6 opacity-0 translate-y-8 transition-all duration-700 group-hover:opacity-100 group-hover:translate-y-0">
                  <div className="w-12 h-0.5 bg-secondary mx-auto mb-4 rounded-md" />
                  <ul className="flex flex-col gap-3 text-left">
                    {handlingPoints.filter(hp => hp.destId === dest.id).map((hp, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-white/95 text-sm font-sans">
                          <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                          <span className="leading-tight">{hp.point}</span>
                        </li>)}
                  </ul>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}